import {
  CSS_PROPERTIES,
  type EditorReactComponent,
  type CssCustomProperties,
  ACTIONS,
  type DisplayFilters as WixComponentManifestDisplayFilters,
} from '@wix/component-protocol/schema';

import type {
  CssCustomPropertyItem,
  ElementParam,
  CustomEnumOptions,
  InnerElement,
  NumberOptions,
  StringEnumOptions,
  InstallationInfo,
  DisplayFilters,
  NumberEnumOptions,
  PresetsGrouping,
  CustomStylesGrouping,
  ElementsGrouping,
} from '..';
import { Breakpoint as OOIBreakpoint, DataType, StyleType } from '../types';
import type {
  CustomRecord,
  NumericValue,
  PropertyTypeValueMap,
} from '../types/metadata/stylesDefinition';
import { mapParamToItem } from './conversion-utils';
import {
  type Data,
  type DataItem,
  type PresetConfig,
  type PresetItem,
  type Presets,
  type PresetsParams,
  type SettingParams,
  type StyleParams,
  type Grouping,
  type DisplayGroupsParams,
  type DisplayGroups,
  type DisplayGroupItem,
  DISPLAY_GROUPS,
  LAYOUT,
  type Breakpoint,
  type PresetStyleOverridesElements,
  GroupType,
  StyleItemDefaultValue,
} from './types';
import { ResolvedStyleTypeMetadata } from '../types/metadata/metadataDefinition';
import { resolveStyleCSSValue } from '../resolvers';

export interface MapperOptions {
  isMobile?: boolean;
  paramsData: Record<string, ElementParam<any, any>>;
}

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

type NumericStyleTypes = StyleTypeFor<NumericValue>;

const NUMERIC_STYLE_TYPES = new Set<NumericStyleTypes>([
  // layout properties
  StyleType.width,
  StyleType.height,
  StyleType.gap,

  // text properties
  StyleType.fontSize,
  StyleType.fontWeight,
  StyleType.lineHeight,
  StyleType.letterSpacing,

  // border properties
  StyleType.borderWidth,
  StyleType.borderRadius,
  StyleType.borderTopLeftRadius,
  StyleType.borderTopRightRadius,
  StyleType.borderBottomLeftRadius,
  StyleType.borderBottomRightRadius,
  StyleType.borderStartStartRadius,
  StyleType.borderStartEndRadius,
  StyleType.borderEndStartRadius,
  StyleType.borderEndEndRadius,

  // margin properties
  StyleType.margin,
  StyleType.marginTop,
  StyleType.marginRight,
  StyleType.marginBottom,
  StyleType.marginLeft,
  StyleType.marginInlineStart,
  StyleType.marginInlineEnd,

  // padding properties
  StyleType.padding,
  StyleType.paddingTop,
  StyleType.paddingRight,
  StyleType.paddingBottom,
  StyleType.paddingLeft,
  StyleType.paddingInlineStart,
  StyleType.paddingInlineEnd,

  // non-css properties
  StyleType.number,
]);

export const isNumericType = (type: StyleType): boolean =>
  NUMERIC_STYLE_TYPES.has(type as NumericStyleTypes);

function setCommonValues({
  isMobile,
  defaultValue,
  displayName,
}: {
  isMobile?: boolean;
  defaultValue?: StyleItemDefaultValue;
  displayName?: string;
}): CssCustomPropertyItem {
  return {
    ...(typeof isMobile === 'boolean' && { defaultValue }),
    ...(displayName && { displayName }),
  };
}

type EnumMetadata = Extract<
  ResolvedStyleTypeMetadata,
  {
    type:
      | StyleType.customEnum
      | StyleType.numberEnum
      | StyleType.stringEnum
      | StyleType.legacyFontStyleParamEnum;
  }
>;

function areIterableOptions(
  options?: ElementParam<any, any>['options'],
): options is StringEnumOptions | NumberEnumOptions {
  if (typeof options === 'undefined') {
    return false;
  }
  return Array.isArray(options);
}

function isCustomEnumMetadata(
  metadata?: ResolvedStyleTypeMetadata,
): metadata is Extract<
  ResolvedStyleTypeMetadata,
  {
    type: StyleType.customEnum;
  }
> {
  return metadata !== undefined && metadata.type === StyleType.customEnum;
}

function isCustomEnumOptions<Settings, Styles>(
  options?: ElementParam<Settings, Styles>['options'],
): options is CustomEnumOptions {
  return options !== undefined && 'type' in options;
}
function setCustomEnumItem({
  isMobile,
  defaultValue,
  displayName,
  param,
  metadata,
}: {
  defaultValue?: StyleItemDefaultValue;
  isMobile?: boolean;
  displayName?: string;
  param: ElementParam<any, any>;
  metadata?: EnumMetadata;
}): CssCustomPropertyItem {
  const customEnumBase = {
    cssPropertyType: CSS_PROPERTIES.CSS_PROPERTY_TYPE.customEnum,
    ...setCommonValues({
      isMobile,
      defaultValue,
      displayName,
    }),
  };
  if (isCustomEnumMetadata(metadata)) {
    const options = isCustomEnumOptions(param.options)
      ? param.options
      : undefined;
    return {
      ...customEnumBase,
      customEnum: {
        cssPropertyType: options?.type,
        options: options?.items?.map((o) => ({
          value: String(o.value),
          displayName: o.displayName,
        })),
      },
    };
  }

  const fallbackCssPropertyFromStyleType =
    metadata?.type === StyleType.numberEnum
      ? CSS_PROPERTIES.CSS_PROPERTY_TYPE.number
      : CSS_PROPERTIES.CSS_PROPERTY_TYPE.string;

  const fallbackParamOptions = areIterableOptions(param?.options)
    ? param.options.map((o) => ({
        value: String(o.value),
        displayName: o.displayName,
      }))
    : undefined;
  return {
    ...customEnumBase,
    customEnum: {
      cssPropertyType: fallbackCssPropertyFromStyleType,
      options: fallbackParamOptions,
    },
  };
}

function setNumericItem({
  type,
  isMobile,
  defaultValue,
  displayName,
  param,
}: {
  type: StyleType;
  defaultValue?: StyleItemDefaultValue;
  isMobile?: boolean;
  displayName?: string;
  param: ElementParam<any, any>;
}): CssCustomPropertyItem {
  return {
    cssPropertyType:
      type as unknown as CssCustomPropertyItem['cssPropertyType'],
    ...setCommonValues({
      isMobile,
      defaultValue: resolveStyleCSSValue({ type, defaultValue }),
      displayName,
    }),
    number: param.options as NumberOptions,
  };
}

const isEnumMetadata = (
  metadata?: ResolvedStyleTypeMetadata,
): metadata is EnumMetadata =>
  metadata !== undefined &&
  [
    StyleType.customEnum,
    StyleType.numberEnum,
    StyleType.stringEnum,
    StyleType.legacyFontStyleParamEnum,
  ].includes(metadata.type);

export const mapOOIStylesToManifestStyles = (
  params: StyleParams,
  { isMobile, paramsData = {} }: MapperOptions,
): CssCustomProperties => {
  const styles: CssCustomProperties = {};

  for (const [key, { metadata }] of Object.entries(params)) {
    const param = paramsData[key] || {};
    const { displayName } = param;

    const styleItem = mapParamToItem({
      metadata,
      params,
      isMobile: isMobile ?? false,
      validate: (type) => type in StyleType,
      createItem: (type, defaultValue) => {
        if (isEnumMetadata(metadata)) {
          return setCustomEnumItem({
            isMobile,
            defaultValue,
            displayName,
            param,
            metadata,
          });
        }

        if (type === StyleType.booleanValue) {
          return {
            cssPropertyType: CSS_PROPERTIES.CSS_PROPERTY_TYPE.display,
            display: {
              displayValues: ['block', 'none'] as NonNullable<
                CssCustomPropertyItem['display']
              >['displayValues'],
            },
            defaultValue: defaultValue ? 'block' : 'none',
          };
        }

        if (isNumericType(type)) {
          return setNumericItem({
            type,
            isMobile,
            defaultValue,
            displayName,
            param,
          });
        }
        return {
          cssPropertyType:
            type as unknown as CssCustomPropertyItem['cssPropertyType'],
          ...setCommonValues({
            isMobile,
            defaultValue,
            displayName,
          }),
        };
      },
    });

    if (styleItem) {
      styles[key] = styleItem;
    }
  }

  return styles;
};

export const mapOOISettingsToManifestData = (
  params: SettingParams,
  { isMobile, paramsData = {} }: MapperOptions,
): Data => {
  const data: Data = {};

  for (const [key, { metadata }] of Object.entries(params)) {
    const param = paramsData[key] || {};
    const { displayName } = param;

    const dataItem = mapParamToItem({
      metadata,
      params,
      isMobile: isMobile ?? false,
      validate: (type) => type in DataType,
      createItem: (type, defaultValue) => {
        const item: DataItem = {
          dataType: type,
          ...(typeof isMobile === 'boolean' && { defaultValue }),
          ...(displayName && { displayName }),
          ...(metadata?.dataItemManifestData || {}),
        };
        if (type === DataType.textEnum) {
          item.textEnum = {
            options: param.options as StringEnumOptions,
          };
        } else if (type === DataType.number) {
          item.number = param.options as NumberOptions;
        }
        return item;
      },
    });

    if (dataItem) {
      data[key] = dataItem as DataItem;
    }
  }

  return data;
};

const mapOOIGroupingToManifestDisplayGroupItem = (
  type: string,
  restOfGrouping: Omit<Grouping, 'id' | 'type' | 'displayName'>,
): Omit<DisplayGroupItem, 'groupType' | 'displayName'> => {
  switch (type) {
    case 'elements':
      return {
        elements: {
          items: (restOfGrouping as ElementsGrouping).elements,
        },
      };
    case 'presets':
      return {
        presets: {
          items: (restOfGrouping as PresetsGrouping).presets,
        },
      };
    case 'cssCustomProperties':
      return {
        cssCustomProperties: {
          items: (restOfGrouping as CustomStylesGrouping).cssCustomProperties,
        },
      };
  }
  return {};
};

const mapOOIGroupingTypeToManifestGroupType = (type: string): GroupType => {
  switch (type) {
    case 'elements':
      return DISPLAY_GROUPS.GROUP_TYPE.elements;
    case 'presets':
      return DISPLAY_GROUPS.GROUP_TYPE.presets;
    case 'cssCustomProperties':
      return DISPLAY_GROUPS.GROUP_TYPE.cssCustomProperties;
    default:
      throw new Error(`unsupoorted groupType: ${type}`);
  }
};

export const mapOOIGroupingsToManifestDisplayGroups = (
  params: DisplayGroupsParams,
): DisplayGroups =>
  params.reduce(
    (acc, { id, type, displayName, ...restOfGrouping }) => ({
      ...acc,
      [id]: {
        groupType: mapOOIGroupingTypeToManifestGroupType(type),
        displayName,
        ...mapOOIGroupingToManifestDisplayGroupItem(type, restOfGrouping),
      },
    }),
    {},
  );

const mapOOIBreakpointStyleToManifestBreakpointStyle = (style: CustomRecord) =>
  Object.fromEntries(
    Object.entries(style).map(([key, value]) => [key, { value }]),
  );

export const mapOOIDisplayFiltersToManifestDisplayFilters = (
  displayFilters?: DisplayFilters,
): WixComponentManifestDisplayFilters => {
  const designActionName = ACTIONS.ACTION_NAME.design;

  if (!displayFilters) {
    return { actions: { hide: [designActionName] } };
  }

  const existingActions = displayFilters.actions;

  if (!existingActions) {
    return {
      ...displayFilters,
      actions: { hide: [designActionName] },
    };
  }

  if ('hide' in existingActions) {
    return {
      ...displayFilters,
      actions: {
        hide: existingActions.hide.includes(designActionName)
          ? existingActions.hide
          : [...existingActions.hide, designActionName],
      },
    };
  }

  if (existingActions.show.includes(designActionName)) {
    const showArray = existingActions.show.filter(
      (action) => action !== designActionName,
    );
    return {
      ...displayFilters,
      actions:
        showArray.length > 0
          ? { show: showArray }
          : { hide: [designActionName] },
    };
  }

  return displayFilters;
};

const filterStyleForInnerElement = (
  style: CustomRecord,
  innerElement: InnerElement<any, any>,
): CustomRecord =>
  Object.fromEntries(
    Object.entries(style).filter(([key, _value]) =>
      (innerElement.elementParams ?? []).some((param) => param.key === key),
    ),
  );

const filterStyleForRootElement = (
  style: CustomRecord,
  innerElements: InnerElement<any, any>[],
): CustomRecord =>
  Object.fromEntries(
    Object.entries(style).filter(
      ([key, _value]) =>
        !innerElements.some((innerElement) =>
          (innerElement.elementParams ?? []).some((param) => param.key === key),
        ),
    ),
  );

const mapOOIInnerElementsToManifestElements = (
  style: CustomRecord,
  innerElements: InnerElement<any, any>[],
): PresetStyleOverridesElements =>
  innerElements.reduce((acc, innerElement) => {
    const elementStyle = filterStyleForInnerElement(style, innerElement);
    const cssCustomProperties =
      mapOOIBreakpointStyleToManifestBreakpointStyle(elementStyle);

    return {
      ...acc,
      ...(Object.keys(cssCustomProperties).length
        ? { [innerElement.id]: { cssCustomProperties } }
        : {}),
    };
  }, {});

const mapOOIPresetStyleToManifestPresetStyleOverrides = (
  breakpointStyles: PresetConfig['style'],
  innerElements: InnerElement<any, any>[],
): NonNullable<PresetItem['styleOverrides']> =>
  Object.fromEntries(
    Object.entries(breakpointStyles)
      .map(([breakpoint, style]) => {
        const rootStyle = filterStyleForRootElement(style, innerElements);
        const cssCustomProperties =
          mapOOIBreakpointStyleToManifestBreakpointStyle(rootStyle);
        const elements = mapOOIInnerElementsToManifestElements(
          style,
          innerElements,
        );

        const breakpointStyleOverrides = {
          ...(Object.keys(cssCustomProperties).length
            ? { cssCustomProperties }
            : {}),
          ...(Object.keys(elements).length ? { elements } : {}),
        };

        return [breakpoint, breakpointStyleOverrides];
      })
      .filter(
        ([_, breakpointStyleOverrides]) =>
          Object.keys(breakpointStyleOverrides).length,
      ),
  );

const mapOOIBreakpointToManifestBreakpoint = (
  breakpoint: OOIBreakpoint,
): Breakpoint => {
  switch (breakpoint) {
    case OOIBreakpoint.small:
      return LAYOUT.BREAKPOINT.small;
    case OOIBreakpoint.large:
      return LAYOUT.BREAKPOINT.large;
    default:
      return LAYOUT.BREAKPOINT.UNKNOWN_Breakpoint;
  }
};

const mapOOIPresetOptimizedForToManifestOptimizedFor = (
  optimizedFor: PresetConfig['optimizedFor'],
): NonNullable<PresetItem['optimizedFor']> =>
  (optimizedFor ?? []).map(mapOOIBreakpointToManifestBreakpoint);

export const mapOOIPresetsToManifestPresets = (
  params: PresetsParams,
  innerElements: InnerElement<any, any>[],
): Presets =>
  params.reduce(
    (
      acc: Presets,
      { id, style, optimizedFor, ai, ...resetOfPresetConfig }: PresetConfig,
    ) => {
      const mappedOptimizedFor =
        mapOOIPresetOptimizedForToManifestOptimizedFor(optimizedFor);

      const styleOverrides = mapOOIPresetStyleToManifestPresetStyleOverrides(
        style,
        innerElements,
      );

      return {
        ...acc,
        [id]: {
          ...resetOfPresetConfig,
          ...(mappedOptimizedFor.length
            ? { optimizedFor: mappedOptimizedFor }
            : {}),
          ...(Object.keys(styleOverrides).length ? { styleOverrides } : {}),
        },
      };
    },
    {},
  );

export const mapOOIInstallationToManifestInstallation = (
  installation: InstallationInfo,
): EditorReactComponent['installation'] => {
  const { presets, ...restOfInstallation } = installation;

  return {
    ...restOfInstallation,
    ...(presets
      ? {
          presets: {
            default: presets.default ?? null,
            large: presets.default ?? null,
            small: presets.small ?? null,
          },
        }
      : {}),
  };
};
